import { ref, onMounted } from 'vue'
import { apiClient } from '../utils/apiClient.js'

const licenseStatus = ref(null)
const warnings = ref([])
const isLoading = ref(false)
const hasChecked = ref(false)
const error = ref(null)

export function useLicense() {
  const fetchLicenseStatus = async () => {
    // Don't fetch if not authenticated - prevents redirect loop on login page
    if (!apiClient.isAuthenticated()) {
      return null
    }

    if (hasChecked.value) return licenseStatus.value

    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.get('/v1/license/status')
      const data = response?.data
      if (data) {
        licenseStatus.value = data
        warnings.value = data.warnings || []
        hasChecked.value = true
        return data
      }
      return null
    } catch (err) {
      console.error('Failed to fetch license status:', err)

      // Handle license-specific errors
      if (err.isLicenseError) {
        error.value = err.detail || err.message
      } else if (err.status === 403) {
        error.value = 'Unable to verify license. Please try logging in again.'
      } else {
        error.value = 'Failed to check license status'
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Force refresh license status (useful after context switch)
   */
  const refreshLicenseStatus = async () => {
    hasChecked.value = false
    licenseStatus.value = null
    warnings.value = []
    error.value = null
    return fetchLicenseStatus()
  }

  /**
   * Clear license state (call on logout)
   */
  const clearLicenseState = () => {
    hasChecked.value = false
    licenseStatus.value = null
    warnings.value = []
    error.value = null
  }

  const hasWarnings = () => {
    return warnings.value.length > 0
  }

  const getExpiredModules = () => {
    return warnings.value.filter((w) => w.is_expired)
  }

  const getExpiringModules = () => {
    return warnings.value.filter((w) => !w.is_expired && w.days_remaining <= 30)
  }

  const getWarningType = (warning) => {
    if (warning.is_expired) return 'error'
    if (warning.days_remaining <= 7) return 'warning'
    return 'info'
  }

  const getWarningMessage = (warning) => {
    if (warning.is_expired) {
      return `Your ${warning.module_name} license has expired. Please contact support to renew.`
    }
    if (warning.days_remaining <= 7) {
      return `Your ${warning.module_name} license expires in ${warning.days_remaining} days. Please renew soon.`
    }
    return `Your ${warning.module_name} license expires in ${warning.days_remaining} days.`
  }

  // Auto-fetch on mount if not already checked
  onMounted(() => {
    if (!hasChecked.value) {
      fetchLicenseStatus()
    }
  })

  return {
    licenseStatus,
    warnings,
    isLoading,
    hasChecked,
    error,
    fetchLicenseStatus,
    refreshLicenseStatus,
    clearLicenseState,
    hasWarnings,
    getExpiredModules,
    getExpiringModules,
    getWarningType,
    getWarningMessage,
  }
}
